/**
 * API缓存配置常量
 */

// 缓存TTL配置（毫秒）
export const CACHE_TTL = {
  // 短期缓存（1分钟）- 用于频繁变化的数据
  SHORT: 1 * 60 * 1000,

  // 中期缓存（5分钟）- 用于一般数据
  MEDIUM: 5 * 60 * 1000,

  // 长期缓存（30分钟）- 用于相对稳定的数据
  LONG: 30 * 60 * 1000,

  // 超长期缓存（2小时）- 用于很少变化的数据
  EXTRA_LONG: 2 * 60 * 60 * 1000,
} as const;

// 不同API的缓存策略配置
export const API_CACHE_CONFIG = {
  // 模型相关API - 数据相对稳定，使用长期缓存
  '/model/list': {
    ttl: CACHE_TTL.LONG,
    useCache: true,
  },

  // 资产列表API - 数据变化较频繁，使用中期缓存
  '/asset/list': {
    ttl: CACHE_TTL.MEDIUM,
    useCache: true,
  },

  // 分组相关API - 数据变化不频繁，使用长期缓存
  '/group/list': {
    ttl: CACHE_TTL.LONG,
    useCache: true,
  },

  // 用户信息API - 数据相对稳定，使用长期缓存
  '/profile': {
    ttl: CACHE_TTL.LONG,
    useCache: true,
  },

  // 生成任务查询API - 数据变化频繁，使用短期缓存
  '/generation/query': {
    ttl: CACHE_TTL.SHORT,
    useCache: true,
  },

  // 聊天历史API - 数据变化较频繁，使用中期缓存
  '/chat/history': {
    ttl: CACHE_TTL.MEDIUM,
    useCache: true,
  },
} as const;

// 缓存管理器配置
export const CACHE_MANAGER_CONFIG = {
  // 默认TTL
  defaultTTL: CACHE_TTL.MEDIUM,

  // 最大缓存大小（MB）
  maxSize: 50,

  // 最大缓存项数量
  maxItems: 1000,

  // 清理间隔（毫秒）
  cleanupInterval: 2 * 60 * 1000, // 2分钟
} as const;

// 请求去重配置
export const REQUEST_DEDUPLICATION_CONFIG = {
  // 请求超时时间（毫秒）
  timeout: 30 * 1000, // 30秒

  // 是否启用请求去重
  enabled: true,
} as const;

// 预加载配置
export const PRELOAD_CONFIG = {
  // 是否启用智能预加载
  enabled: true,

  // 预加载延迟（毫秒）
  delay: 1000, // 1秒后开始预加载

  // 预加载的常用API列表
  commonApis: [
    {
      url: '/model/list',
      params: { type_tags: ['image'] },
      ttl: CACHE_TTL.LONG,
    },
    {
      url: '/model/list',
      params: { type_tags: ['video'] },
      ttl: CACHE_TTL.LONG,
    },
    {
      url: '/asset/list',
      params: { page_number: 1, page_size: 20, time_desc: true },
      ttl: CACHE_TTL.MEDIUM,
    },
  ],
} as const;

// 缓存失效策略配置
export const CACHE_INVALIDATION_CONFIG = {
  // 写操作后需要失效的相关缓存模式
  invalidationPatterns: {
    // 资产相关操作
    asset: ['/asset/list', '/asset/', '/group/list'],

    // 分组相关操作
    group: ['/group/list', '/asset/list'],

    // 生成相关操作
    generation: ['/generation/', '/asset/list'],

    // 收藏相关操作
    favorite: ['/asset/list'],
  },
} as const;

// 开发环境调试配置
export const DEBUG_CONFIG = {
  // 是否启用缓存日志
  enableLogging: process.env.NODE_ENV === 'development',

  // 是否显示缓存统计
  showStats: process.env.NODE_ENV === 'development',

  // 日志级别
  logLevel: 'info' as 'debug' | 'info' | 'warn' | 'error',
} as const;

/**
 * 根据URL获取缓存配置
 */
export const getCacheConfigForUrl = (url: string) => {
  // 移除查询参数，只匹配路径
  const path = url.split('?')[0];

  // 精确匹配
  if (API_CACHE_CONFIG[path as keyof typeof API_CACHE_CONFIG]) {
    return API_CACHE_CONFIG[path as keyof typeof API_CACHE_CONFIG];
  }

  // 模糊匹配
  for (const [pattern, config] of Object.entries(API_CACHE_CONFIG)) {
    if (path.startsWith(pattern)) {
      return config;
    }
  }

  // 默认配置
  return {
    ttl: CACHE_MANAGER_CONFIG.defaultTTL,
    useCache: true,
  };
};

/**
 * 检查URL是否应该被缓存
 */
export const shouldCacheUrl = (url: string, method: string = 'GET'): boolean => {
  // 只缓存GET请求
  if (method.toUpperCase() !== 'GET') {
    return false;
  }

  const config = getCacheConfigForUrl(url);
  return config.useCache;
};

/**
 * 获取URL的缓存TTL
 */
export const getCacheTTLForUrl = (url: string): number => {
  const config = getCacheConfigForUrl(url);
  return config.ttl;
};
